import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import toast from "react-hot-toast";
import { Spin } from "antd";
import LoadableButton from "../../../components/button/LoadableButton";
import CreateFile from "../../../assets/CreateFile";
import CopyFileModal from "./CopyFileModal";
import {
  GetServiceTypeListApi,
  CreateFileApi,
} from "../../../api/request/service";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useDispatch } from "react-redux";
import { setIsBookKeeping } from "../../../store/Slices/userSlice";
import useWindowWidth from "../../../components/common/UseWindowWidth";

const CreateFiles = ({ type = "" }) => {
  const width = useWindowWidth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [service, setService] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [isCopyDetails, setIsCopyDetails] = useState(false);
  const [selectedService, setSelectedService] = useState([]);
  const IconColor = ["#1E4387", "#0BAFD4", "#40CD7F", "#7367F0"];
  const adjustedWidth = width < 640 ? width - 50 : "100%";

  const getServiceList = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetServiceTypeListApi();
      if (data?.status) {
        const shuffledColors = shuffleArray(IconColor);
        const servicesWithColor = data?.data?.map((item, index) => {
          const color = shuffledColors[index % shuffledColors.length];
          return { ...item, color };
        });
        setService(servicesWithColor);
        dispatch(
          setIsBookKeeping(
            data?.data?.some(
              (item) => item?.is_bookkeeping && item?.client_file_count > 0
            )
          )
        );
      }
      setIsLoading(false);
    } catch (error) {
      setService([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getServiceList();
  }, []);

  const createFileHandler = async (item) => {
    try {
      setLoadingItemId(item.id);
      setIsCreateLoading(true);
      let params = {
        service_type_id: item?.id,
      };
      const { data } = await CreateFileApi(params);
      if (data?.status) {
        toast?.success(data?.message);
        navigate(`/file/${data?.data?.id}`);
        dispatch(data?.data?.is_bookkeeping && setIsBookKeeping(true));
      }
      setIsCreateLoading(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast?.error(error?.response?.data?.message);
      }
      setIsCreateLoading(false);
    }
  };

  const checkFileCount = (item) => {
    if (item?.client_file_count && item?.is_auto_fill) {
      setSelectedService(item);
      setIsCopyDetails(true);
    } else {
      createFileHandler(item);
    }
  };

  const shuffleArray = (array) => {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
      const media = window.matchMedia(query);
      if (media.matches !== matches) {
        setMatches(media.matches);
      }
      const listener = () => setMatches(media.matches);
      media.addEventListener("change", listener);
      return () => media.removeEventListener("change", listener);
    }, [matches, query]);

    return matches;
  };

  const isMd = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const isSm = useMediaQuery("(max-width: 767px)");

  const shouldUseSlider =
    (isMd && service?.length > 2) ||
    (isSm && service?.length > 1) ||
    (!isMd && !isSm && service?.length > 3);

  const shouldUseSliderFile =
    (isMd && service?.length > 2) ||
    (isSm && service?.length > 1) ||
    (!isMd && !isSm && service?.length > 4);

  const sliderSettings = {
    dots: false,
    autoplaySpeed: 3000,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: type === "file" ? shouldUseSliderFile : shouldUseSlider,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    infinite: true,
    autoplay: true,
    variableWidth: false,
    responsive: [
      {
        breakpoint: 1440,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 1020,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 640,
        settings: { slidesToShow: 1 },
      },
      {
        breakpoint: 375,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  const ServiceCard = ({ item, loadingItemId, isCreateLoading }) => (
    <div className="flex flex-col justify-between p-4 border rounded-md cursor-pointer w-full h-full min-h-[250px] bg-white">
      <div className="flex flex-col items-center flex-grow">
        <div className="p-2 bg-[#d9dcdd81] rounded-md mb-2">
          <CreateFile color={"#959FA3"} />
        </div>
        <p
          title={item?.name}
          className="text-[20px] font-semibold text-center capitalize"
        >
          {item?.name}
        </p>
        <p
          className="text-[14px] text-center text-Gray flex-grow h-[20px] line-clamp-4"
          title={item?.description}
        >
          {item?.description}
        </p>
      </div>

      <LoadableButton
        lable="Create File"
        className="text-sm py-2 px-4 mt-4 w-full"
        onClick={() => checkFileCount(item)}
        loadingLable="Creating file..."
        isLoading={loadingItemId === item.id ? isCreateLoading : false}
        isLoadingLableResponsive={true}
      />
    </div>
  );

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);

  return (
    <div className="p-6 bg-white border rounded-xl relative overflow-hidden max-sm:p-3">
      <div className="flex items-center justify-between mb-4 ">
        {type !== "files" ? (
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-green-500 to-purple-500 rounded-t-full bg-[length:200%_200%] animate-gradient-x"></div>
        ) : null}

        <h2 className="text-xl font-medium">Available Services</h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[250px]">
          <Spin size="medium" />
        </div>
      ) : !service?.length ? (
        <div className="flex flex-col items-center justify-center text-gray-500 h-full">
          <img
            src="/assets/images/no_data.png"
            alt="no data"
            className="w-30 h-30 sm:w-52 sm:h-52 mb-3 opacity-70"
          />
          <p>No services found</p>
        </div>
      ) : type === "files" ? (
        shouldUseSliderFile ? (
          <div className="h-[250px] w-full" style={{ maxWidth: adjustedWidth }}>
            <Slider {...sliderSettings}>
              {service?.map((item, index) => (
                <div key={`service_${index}`} className="px-2">
                  <ServiceCard
                    item={item}
                    loadingItemId={loadingItemId}
                    isCreateLoading={isCreateLoading}
                  />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="grid gap-5 grid-cols-5 max-lg:grid-cols-3 max-sm:grid-cols-1 w-full h-full bg-white">
            {service?.map((item, index) => (
              <ServiceCard
                key={`service_${index}`}
                item={item}
                loadingItemId={loadingItemId}
                isCreateLoading={isCreateLoading}
              />
            ))}
          </div>
        )
      ) : shouldUseSlider ? (
        <div className="h-[250px] w-full" style={{ maxWidth: adjustedWidth }}>
          <Slider {...sliderSettings}>
            {service?.map((item, index) => (
              <div key={`service_${index}`} className="px-2">
                <ServiceCard
                  item={item}
                  loadingItemId={loadingItemId}
                  isCreateLoading={isCreateLoading}
                />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 w-full h-full bg-white">
          {service?.map((item, index) => (
            <ServiceCard
              key={`service_${index}`}
              item={item}
              loadingItemId={loadingItemId}
              isCreateLoading={isCreateLoading}
            />
          ))}
        </div>
      )}

      <CopyFileModal
        isOpen={isCopyDetails}
        setIsOpen={setIsCopyDetails}
        data={selectedService}
        createFileHandler={createFileHandler}
        isCreateLoading={isCreateLoading}
      />
    </div>
  );
};

const CustomPrevArrow = ({ onClick }) => (
  <div
    className="absolute right-11 -top-8 transform -translate-y-1/2 z-50 cursor-pointer bg-[#E5FAFF] h-8 w-8 rounded-full flex justify-center items-center"
    onClick={onClick}
  >
    <img src="/assets/icons/left-angle.svg" alt="prev" />
  </div>
);

const CustomNextArrow = ({ onClick }) => (
  <div
    className="absolute right-0 -top-8 transform -translate-y-1/2 z-50 cursor-pointer bg-[#E5FAFF] h-8 w-8 rounded-full flex justify-center items-center"
    onClick={onClick}
  >
    <img src="/assets/icons/right-angle.svg" alt="next" />
  </div>
);

export default CreateFiles;
